import React, { useMemo, useState } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Filler,
} from "chart.js";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Filler
);

/**
 * Renders a chart spec returned by POST /ai-vector/chat.
 *
 * The backend supplies labels, values and colours; this component only decides
 * geometry and typography. It never computes or reshapes the numbers.
 *
 * Colour rules baked in (validated palette, see api/ai_vector/charts.py):
 *  - single measure across categories = ONE hue, not a rainbow
 *  - doughnut slices use the fixed categorical order, capped, tail = "Other"
 *  - some light-mode hues fall below 3:1 on white, so values are always visible
 *    and a table view is always available
 */

const INK = {
  light: { primary: "#0b0b0b", secondary: "#52514e", grid: "#e6e5e1", surface: "#ffffff" },
  dark: { primary: "#ffffff", secondary: "#c3c2b7", grid: "#33322f", surface: "#1a1a19" },
};

const AiVectorChart = ({ spec, mode = "light" }) => {
  const [showTable, setShowTable] = useState(false);

  const ink = INK[mode] || INK.light;
  const colors = spec?.colors?.[mode] ?? spec?.colors?.light;

  const data = useMemo(() => {
    if (!spec) return null;

    const isDoughnut = spec.type === "doughnut";
    const isLine = spec.type === "line";

    return {
      labels: spec.labels,
      datasets: [
        {
          label: spec.value_label || "Count",
          data: spec.values,
          backgroundColor: isLine ? "transparent" : colors,
          borderColor: isLine ? colors : ink.surface,
          // A 2px surface-coloured gap keeps adjacent fills from bleeding together.
          borderWidth: isDoughnut ? 2 : isLine ? 2 : 0,
          // 4px rounded data-ends, anchored flat to the baseline.
          borderRadius: isDoughnut || isLine ? 0 : 4,
          borderSkipped: false,
          barThickness: spec.type === "horizontalBar" ? 18 : 26,
          maxBarThickness: 32,
          pointRadius: isLine ? 4 : 0,
          pointHoverRadius: isLine ? 6 : 0,
          pointBackgroundColor: colors,
          pointBorderColor: ink.surface,
          pointBorderWidth: 2,
          tension: 0.25,
          fill: false,
          cutout: isDoughnut ? "62%" : undefined,
        },
      ],
    };
  }, [spec, colors, ink.surface]);

  const options = useMemo(() => {
    if (!spec) return {};

    const isDoughnut = spec.type === "doughnut";
    const horizontal = spec.type === "horizontalBar";

    return {
      indexAxis: horizontal ? "y" : "x",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 8, right: 16, bottom: 4, left: 4 } },
      plugins: {
        legend: {
          // A single series is named by the title; a legend would repeat it.
          display: Boolean(spec.show_legend),
          position: isDoughnut ? "right" : "bottom",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            padding: 12,
            usePointStyle: true,
            // Legend text stays in ink, never the series colour.
            color: ink.secondary,
            font: { size: 12 },
            generateLabels: (chart) =>
              chart.data.labels.map((label, i) => ({
                text: `${label} — ${chart.data.datasets[0].data[i]}`,
                fillStyle: Array.isArray(colors) ? colors[i] : colors,
                strokeStyle: ink.surface,
                lineWidth: 2,
                pointStyle: "circle",
                index: i,
              })),
          },
        },
        tooltip: {
          backgroundColor: mode === "dark" ? "#000000" : "#111111",
          titleColor: "#ffffff",
          bodyColor: "#ffffff",
          padding: 10,
          displayColors: true,
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.parsed;
              const pct = spec.total ? ((value / spec.total) * 100).toFixed(1) : null;
              return pct ? ` ${value} (${pct}%)` : ` ${value}`;
            },
          },
        },
      },
      scales: isDoughnut
        ? {}
        : {
            x: {
              beginAtZero: true,
              grid: {
                display: horizontal,
                color: ink.grid,
                drawBorder: false,
              },
              ticks: { color: ink.secondary, font: { size: 11 }, maxRotation: 0, autoSkip: true },
              title: horizontal
                ? { display: true, text: spec.value_label, color: ink.secondary, font: { size: 11 } }
                : undefined,
            },
            y: {
              beginAtZero: true,
              grid: {
                display: !horizontal,
                color: ink.grid,
                drawBorder: false,
              },
              ticks: { color: ink.secondary, font: { size: 11 }, precision: 0 },
              title: !horizontal
                ? { display: true, text: spec.value_label, color: ink.secondary, font: { size: 11 } }
                : undefined,
            },
          },
    };
  }, [spec, colors, ink, mode]);

  if (!spec || !data) return null;

  const ChartComponent =
    spec.type === "doughnut" ? Doughnut : spec.type === "line" ? Line : Bar;

  const height = spec.type === "horizontalBar" ? Math.max(220, spec.labels.length * 34) : 280;

  return (
    <div
      style={{
        border: `1px solid ${ink.grid}`,
        borderRadius: 10,
        padding: "14px 16px 10px",
        marginTop: 12,
        background: ink.surface,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: ink.primary }}>{spec.title}</div>
          <div style={{ fontSize: 12, color: ink.secondary, marginTop: 2 }}>
            {spec.total} total
            {spec.note ? ` · ${spec.note}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          style={{
            fontSize: 11,
            color: ink.secondary,
            background: "transparent",
            border: `1px solid ${ink.grid}`,
            borderRadius: 6,
            padding: "3px 9px",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {showTable ? "Chart" : "Table"}
        </button>
      </div>

      {showTable ? (
        <table style={{ width: "100%", fontSize: 13, marginTop: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "6px 4px", color: ink.secondary, fontWeight: 500, borderBottom: `1px solid ${ink.grid}` }}>
                {spec.axis_label}
              </th>
              <th style={{ textAlign: "right", padding: "6px 4px", color: ink.secondary, fontWeight: 500, borderBottom: `1px solid ${ink.grid}` }}>
                {spec.value_label}
              </th>
            </tr>
          </thead>
          <tbody>
            {spec.labels.map((label, i) => (
              <tr key={`${label}-${i}`}>
                <td style={{ padding: "6px 4px", color: ink.primary, borderBottom: `1px solid ${ink.grid}` }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      marginRight: 8,
                      background: Array.isArray(colors) ? colors[i] : colors,
                    }}
                  />
                  {label}
                </td>
                <td style={{ padding: "6px 4px", textAlign: "right", color: ink.primary, borderBottom: `1px solid ${ink.grid}` }}>
                  {spec.values[i]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ height, marginTop: 8 }}>
          <ChartComponent data={data} options={options} />
        </div>
      )}
    </div>
  );
};

export default AiVectorChart;
